import apiClient from "@/lib/api-client";
import { useLibraryUrlPattern, useZoteroSource } from "@/hooks/useZoteroSource";
import { safeExternalHref } from "@/lib/safeUrl";

/** Where "get it through your library" goes for one DOI: the user's library
 * URL pattern (EZproxy / OpenURL / LibKey, Settings → Integrations) with the
 * DOI substituted, or doi.org when no pattern is set. `notifyZotero` fires
 * poll-now so a PDF that Zotero saves is caught in ~30s instead of waiting
 * up to 5 min for the next scheduled poll.
 */
export function useLibraryLink(doi: string | null | undefined) {
  const { query } = useLibraryUrlPattern();
  const zotero = useZoteroSource();
  const pattern = query.data?.pattern ?? null;
  // The library pattern is user-supplied config, so scheme-check it too —
  // self-XSS only, but it costs one call and keeps every paper-derived href
  // going through the same guard (audit 2026-07-28, MEDIUM-5).
  const href = doi
    ? (safeExternalHref(
        pattern
          ? pattern.replace("{DOI}", encodeURIComponent(doi))
          : `https://doi.org/${encodeURI(doi)}`,
      ) ?? undefined)
    : undefined;

  const notifyZotero = () => {
    apiClient.post("api/zotero-sync/poll-now").catch(() => {
      // Best-effort: fire-and-forget. 409 (no source) and 429 (rate-limited)
      // are silently ignored.
    });
  };

  return {
    href,
    hasPattern: pattern != null,
    zoteroConnected: zotero.data?.status === "active",
    notifyZotero,
  };
}
